import React, { useState, useEffect, useRef, createRef, memo } from "react";
import { Constants, useMeeting, useParticipant, usePubSub } from "@videosdk.live/react-sdk";
import { BottomBar } from "./components/BottomBar";
import { SidebarConatiner } from "../components/sidebar/SidebarContainer";
import MemorizedParticipantView from "./components/ParticipantView";
import { PresenterView } from "../components/PresenterView";
import { nameTructed, trimSnackBarText } from "../utils/helper";
import WaitingToJoinScreen from "../components/screens/WaitingToJoinScreen";
import ConfirmBox from "../components/ConfirmBox";
import useIsMobile from "../hooks/useIsMobile";
import useIsTab from "../hooks/useIsTab";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { useMeetingAppContext } from "../MeetingAppContextDef";

// Toast component for the host to see and act on joining requests
const WaitingParticipantToast = ({ participant, onRespond }) => {
  return (
    <div
      style={{
        position: "fixed",
        bottom: "100px",
        left: "20px",
        backgroundColor: "white",
        padding: "1rem",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "1rem",
      }}
    >
      <span>
        <strong>{participant.name}</strong> wants to join
      </span>
      <button
        style={{ backgroundColor: "#228B22", color: "white", padding: "8px 12px", border: "none", borderRadius: "4px", cursor: "pointer" }}
        onClick={() => {
          participant.allow();
          onRespond(participant.participantId);
        }}
      >
        Allow
      </button>
      <button
        style={{ backgroundColor: "#D32F2F", color: "white", padding: "8px 12px", border: "none", borderRadius: "4px", cursor: "pointer" }}
        onClick={() => {
          participant.deny();
          onRespond(participant.participantId);
        }}
      >
        Deny
      </button>
    </div>
  );
};


export function MeetingContainer({ onMeetingLeave, setIsMeetingLeft }) {
  const { setSelectedMic, setSelectedWebcam, setSelectedSpeaker } = useMeetingAppContext();
  const { useRaisedHandParticipants } = useMeetingAppContext();

  const [participantsData, setParticipantsData] = useState([]);
  const [localParticipantAllowedJoin, setLocalParticipantAllowedJoin] = useState(null);
  const [meetingErrorVisible, setMeetingErrorVisible] = useState(false);
  const [meetingError, setMeetingError] = useState(false);
  const [waitingParticipants, setWaitingParticipants] = useState([]);

  const mMeetingRef = useRef();
  const containerRef = createRef();
  const bottomBarHeight = 60;

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });

  const sideBarContainerWidth = isXLDesktop ? 400 : isLGDesktop ? 360 : isTab ? 320 : isMobile ? 280 : 240;

  const { participantRaisedHand } = useRaisedHandParticipants();

  const _handleMeetingLeft = () => {
    setIsMeetingLeft(true);
  };

  const _handleOnRecordingStateChanged = ({ status }) => {
    if (status === Constants.recordingEvents.RECORDING_STARTED || status === Constants.recordingEvents.RECORDING_STOPPED) {
      toast(`${status === Constants.recordingEvents.RECORDING_STARTED ? "Meeting recording is started" : "Meeting recording is stopped."}`, {
        position: "bottom-left", autoClose: 4000, hideProgressBar: true, closeButton: false, pauseOnHover: true, draggable: true, progress: undefined, theme: "light",
      });
    }
  };

  function onParticipantJoined(participant) {
    participant?.setQuality("high");
    const participantIds = Array.from(mMeeting.participants.keys());
    setParticipantsData(participantIds);
  }

  function onParticipantLeft(participant) {
    const participantIds = Array.from(mMeeting.participants.keys());
    setParticipantsData(participantIds);
  }

  // Eevent handler for HOST to receive joining requests
  function onEntryRequested(data) {
    const { participantId, name, allow, deny } = data;
    setWaitingParticipants((prev) => [...prev, { participantId, name, allow, deny }]);
  }

  // Event handler for GUEST to know if their request was accepted or denied
  function onEntryResponded(participantId, decision) {
    if (mMeetingRef.current?.localParticipant?.id === participantId) {
      if (decision === "allowed") {
        setLocalParticipantAllowedJoin(true);
      } else {
        setLocalParticipantAllowedJoin(false);
        setTimeout(() => {
          _handleMeetingLeft();
        }, 3000);
      }
    }
  }

  function onMeetingJoined() {
    // For hosts or participants who don't need permission, directly allow them.
    if (mMeeting?.localParticipant?.permission?.askToJoin === false) {
      setLocalParticipantAllowedJoin(true);
    }
  }

  function onMeetingLeft() {
    setSelectedMic({ id: null, label: null });
    setSelectedWebcam({ id: null, label: null });
    setSelectedSpeaker({ id: null, label: null });
    onMeetingLeave();
  }

  const _handleOnError = (data) => {
    const { code, message } = data;
    const joiningErrCodes = [4001, 4002, 4003, 4004, 4005, 4006, 4007, 4008, 4009, 4010];
    const isJoiningError = joiningErrCodes.some((c) => c === code);
    const isCriticalError = `${code}`.startsWith("500");

    new Audio(isCriticalError ? `https://static.videosdk.live/prebuilt/notification_critical_err.mp3` : `https://static.videosdk.live/prebuilt/notification_err.mp3`).play();

    setMeetingErrorVisible(true);
    setMeetingError({ code, message: isJoiningError ? "Unable to join meeting!" : message });
  };

  const mMeeting = useMeeting({
    onParticipantJoined,
    onParticipantLeft,
    onEntryRequested, // Hook in the host's event handler
    onEntryResponded,
    onMeetingJoined,
    onMeetingLeft,
    onError: _handleOnError,
    onRecordingStateChanged: _handleOnRecordingStateChanged,
  });

  const isPresenting = mMeeting.presenterId ? true : false;

  useEffect(() => {
    mMeetingRef.current = mMeeting;
  }, [mMeeting]);

  useEffect(() => {
    const participantIds = Array.from(mMeeting.participants.keys());
    setParticipantsData(participantIds);
  }, [mMeeting.participants]);

  // PubSub hooks for chat and raise hand
  usePubSub("RAISE_HAND", {
    onMessageReceived: (data) => {
      const { senderId, senderName } = data;
      new Audio(`https://static.videosdk.live/prebuilt/notification.mp3`).play();
      toast(`${senderId === mMeeting?.localParticipant?.id ? "You" : nameTructed(senderName, 15)} raised hand 🖐🏼`, {
        position: "bottom-left", autoClose: 4000, hideProgressBar: true, closeButton: false, pauseOnHover: true, draggable: true, progress: undefined, theme: "light",
      });
      participantRaisedHand(senderId);
    },
  });

  usePubSub("CHAT", {
    onMessageReceived: (data) => {
      if (data.senderId !== mMeeting?.localParticipant?.id) {
        new Audio(`https://static.videosdk.live/prebuilt/notification.mp3`).play();
        toast(`${trimSnackBarText(`${nameTructed(data.senderName, 15)} says: ${data.message}`)}`, {
          position: "bottom-left", autoClose: 4000, hideProgressBar: true, closeButton: false, pauseOnHover: true, draggable: true, progress: undefined, theme: "light",
        });
      }
    },
  });

  // Function to remove a participant from the waiting list after the host has responded
  const handleLobbyResponse = (participantId) => {
    setWaitingParticipants((prev) => prev.filter((p) => p.participantId !== participantId));
  };
  
  const ParticipantMicStream = memo(({ participantId }) => {
    const { micStream } = useParticipant(participantId);
    useEffect(() => {
      if (micStream) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);
        const audioElement = new Audio();
        audioElement.srcObject = mediaStream;
        audioElement.play().catch(err => console.error("Audio play error", err));
      }
    }, [micStream, participantId]);
    return null;
  }, [participantsData]);


  // Determine the current view based on meeting and lobby status
  const getMeetingView = () => {
    // If the meeting hasn't joined yet, show a generic joining screen
    if (!mMeeting.isMeetingJoined) {
        return <WaitingToJoinScreen message="Joining the meeting..." />;
    }
    
    // If the participant has been explicitly allowed (or is a host), show the main meeting view
    if (localParticipantAllowedJoin === true) {
      return (
        <>
          {/* Host will see notifications here if anyone is waiting */}
          {waitingParticipants.map((participant) => (
            <WaitingParticipantToast
              key={participant.participantId}
              participant={participant}
              onRespond={handleLobbyResponse}
            />
          ))}

          <div className={`flex flex-1 flex-row bg-gray-800`}>
            <div className={`flex flex-1`}>
              {isPresenting ? (
                <PresenterView height={`calc(100vh - ${bottomBarHeight}px)`} />
              ) : null}
              {isPresenting && isMobile ? (
                 participantsData.map((participantId) => (
                  <ParticipantMicStream key={participantId} participantId={participantId} />
                ))
              ) : (
                <MemorizedParticipantView isPresenting={isPresenting} />
              )}
            </div>

            <SidebarConatiner
              height={`calc(100vh - ${bottomBarHeight}px)`}
              sideBarContainerWidth={sideBarContainerWidth}
            />
          </div>

          <BottomBar
            bottomBarHeight={bottomBarHeight}
            setIsMeetingLeft={setIsMeetingLeft}
          />
        </>
      );
    }
    // If the participant was explicitly denied
    else if (localParticipantAllowedJoin === false) {
      return <WaitingToJoinScreen message="You were denied entry to the meeting." />;
    }
    // If waiting for the host's response
    else {
      return <WaitingToJoinScreen message="Waiting to be allowed by the host..." />;
    }
  };

  return (
    <div className="fixed inset-0">
      <div ref={containerRef} className="h-full flex flex-col bg-gray-800">
        {getMeetingView()}
        <ConfirmBox
          open={meetingErrorVisible}
          successText="OKAY"
          onSuccess={() => setMeetingErrorVisible(false)}
          title={`Error Code: ${meetingError.code}`}
          subTitle={meetingError.message}
        />
      </div>
    </div>
  );
}
